/**
 * Read-only render of a Clarity Map exactly as the user built it.
 * Positions match Build exactly via scale transform. Each tile gets
 * a counter-scale so text stays readable at any viewport size.
 */
import { useRef, useEffect, useState } from 'react';

const CANVAS_W = 1200;
const CANVAS_H = 700;
const MID_X = CANVAS_W / 2;

function seededFromId(id, salt = 0) {
  let h = salt;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h % 10000) / 10000;
}

export default function NestedEggCanvas({ tiles, mapName, measurement, initialScale }) {
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(initialScale || 0.65);

  // Use Build's scale if available, otherwise compute from viewport
  const didFit = useRef(false);
  useEffect(() => {
    if (initialScale) { didFit.current = true; setScale(initialScale); return; }
    if (!viewportRef.current) return;
    const el = viewportRef.current;
    const ro = new ResizeObserver((entries) => {
      if (didFit.current) return;
      const r = entries[0].contentRect;
      if (r.width === 0 || r.height === 0) return;
      didFit.current = true;
      const s = Math.min(1.0, r.width / (CANVAS_W + 40), r.height / (CANVAS_H + 40));
      setScale(Math.max(0.65, s));
      ro.disconnect();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [initialScale]);

  function fitAll() {
    if (!viewportRef.current) return;
    const r = viewportRef.current.getBoundingClientRect();
    const s = Math.min(1.0, r.width / (CANVAS_W + 40), r.height / (CANVAS_H + 40));
    setScale(Math.max(0.65, s));
  }

  const zoomPercent = Math.round(scale * 100);

  const bigIdea = tiles.find((t) => t.zone === 'BIG_IDEA');
  const scaffold1 = tiles.find((t) => t.zone === 'SCAFFOLDING_1');
  const scaffold2 = tiles.find((t) => t.zone === 'SCAFFOLDING_2');
  // Include EVERY non-cut tile that isn't already shown in a header slot.
  const usedIds = new Set([bigIdea?.id, scaffold1?.id, scaffold2?.id].filter(Boolean));
  const rawScattered = tiles.filter((t) => t.zone !== 'CUT' && !usedIds.has(t.id));

  // Compute display position for every concept. NO clamping, NO offset — preserve chaos.
  // Tiles with no saved position get a deterministic seeded scatter, in the same
  // CANVAS_W coordinate space the Build view uses.
  const scattered = rawScattered.map((t) => {
    let posX = t.posX || 0;
    let posY = t.posY || 0;
    if (posX === 0 && posY === 0) {
      const rx = seededFromId(t.id, 1);
      const ry = seededFromId(t.id, 7);
      if (t.zone === 'PROOF_1' || t.zone === 'SCAFFOLDING_1') {
        posX = 60 + rx * 440;
        posY = 60 + ry * 480;
      } else if (t.zone === 'PROOF_2' || t.zone === 'SCAFFOLDING_2') {
        posX = MID_X + 60 + rx * 440;
        posY = 60 + ry * 480;
      } else {
        posX = 60 + rx * 1080;
        posY = 60 + ry * 480;
      }
    }
    return { ...t, posX, posY };
  });


  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-canvas">
      {/* === HEADER: outside the scale transform, stays readable at any zoom === */}
      {measurement && (
        <div className="flex-shrink-0 bg-gold/25 border-b-2 border-gold-dark px-5 py-2 text-center shadow-sm">
          <span className="text-xs font-heading font-bold text-gold-dark uppercase tracking-widest mr-3">
            What Does Success Look Like?
          </span>
          <span className="text-base font-semibold text-txt-primary">{measurement}</span>
        </div>
      )}

      <div className="flex-shrink-0 bg-white border-b border-black/15 px-4 py-2">
        <div className="flex items-center justify-center">
          <div
            className="relative rounded-xl flex items-center justify-center px-5 shadow-sm"
            style={{
              minHeight: 36,
              minWidth: 320,
              maxWidth: 560,
              background: 'linear-gradient(90deg, #E87722 0%, #F09A50 50%, #E87722 100%)',
              border: '2px solid #C45E10',
            }}
          >
            <div className="absolute -top-1.5 left-3 px-1.5 text-[8px] font-heading font-bold uppercase tracking-widest text-gold-dark bg-white">
              The Big Idea
            </div>
            {bigIdea ? (
              <div className="text-black font-heading font-bold text-sm uppercase tracking-wide text-center break-words">
                {bigIdea.text}
              </div>
            ) : (
              <span className="text-xs text-black/60 italic font-body font-semibold">No Big Idea set</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 grid grid-cols-2 gap-2 px-3 py-2 bg-white border-b border-black/15">
        <div className="relative rounded-xl flex items-center justify-center text-center px-4 shadow-sm"
          style={{ height: 40, background: scaffold1 ? 'linear-gradient(90deg, #1D9E75 0%, #3FBF94 50%, #1D9E75 100%)' : '#FFF', border: scaffold1 ? '2px solid #157A5A' : '2px dashed #157A5A' }}>
          <div className="absolute -top-1.5 left-3 px-1.5 text-[8px] font-heading font-bold uppercase tracking-widest text-teal-dark bg-white">Scaffold 1</div>
          {scaffold1 ? <div className="text-black font-heading font-bold text-sm uppercase tracking-wide text-center truncate">{scaffold1.text}</div>
            : <span className="text-xs text-teal-dark italic font-body font-semibold">No scaffold set</span>}
        </div>
        <div className="relative rounded-xl flex items-center justify-center text-center px-4 shadow-sm"
          style={{ height: 40, background: scaffold2 ? 'linear-gradient(90deg, #E87722 0%, #F09A50 50%, #E87722 100%)' : '#FFF', border: scaffold2 ? '2px solid #C45E10' : '2px dashed #C45E10' }}>
          <div className="absolute -top-1.5 left-3 px-1.5 text-[8px] font-heading font-bold uppercase tracking-widest text-gold-dark bg-white">Scaffold 2</div>
          {scaffold2 ? <div className="text-black font-heading font-bold text-sm uppercase tracking-wide text-center truncate">{scaffold2.text}</div>
            : <span className="text-xs text-gold-dark italic font-body font-semibold">No scaffold set</span>}
        </div>
      </div>

      {/* === CANVAS: scale transform preserves Build positions, counter-scale keeps tiles readable === */}
      <div ref={viewportRef} className="flex-1 relative overflow-hidden flex items-start justify-center">
        {/* Half washes */}
        <div className="absolute top-0 left-0 w-1/2 h-full bg-teal/40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gold/35 pointer-events-none" />
        <div className="absolute top-0 left-1/2 w-px h-full bg-navy/30 pointer-events-none" />

        {/* Scaled canvas — positions match Build exactly */}
        <div
          className="relative mx-auto"
          style={{
            width: CANVAS_W,
            height: CANVAS_H,
            transform: `scale(${scale})`,
            transformOrigin: 'center top',
          }}
        >
          {scattered.map((tile) => {
            const x = tile.posX;
            const y = tile.posY;
            const isPlaced = tile.zone === 'PROOF_1' || tile.zone === 'PROOF_2';
            const isLeftSide = x + 100 < MID_X;
            let pillClasses;
            if (!isPlaced) {
              pillClasses = 'bg-navy text-white border-white/15';
            } else if (isLeftSide) {
              pillClasses = 'bg-teal text-white border-teal-dark';
            } else {
              pillClasses = 'bg-gold text-black border-gold-dark';
            }
            return (
              <div
                key={tile.id}
                className="absolute"
                style={{ left: x, top: y }}
              >
                <div
                  className={`${pillClasses} rounded-2xl pill-glow border-[3px] px-6 py-3.5 min-w-[160px] flex items-center justify-center whitespace-nowrap`}
                >
                  <span className="text-base font-bold break-words text-center">{tile.text}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Zoom controls */}
        <div className="absolute bottom-3 left-3 z-30 flex items-center bg-white shadow-sm border border-navy/10 rounded-lg overflow-hidden">
          <button onClick={fitAll}
            className="px-2.5 py-2 text-xs text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-all flex items-center gap-1.5 border-r border-navy/10"
            title="Fit all">Fit All</button>
          <button onClick={() => setScale((s) => Math.max(0.3, s - 0.05))}
            className="px-2.5 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-colors border-r border-navy/10 font-medium">&minus;</button>
          <span className="px-3 py-2 text-xs text-txt-secondary tabular-nums min-w-[48px] text-center">{zoomPercent}%</span>
          <button onClick={() => setScale((s) => Math.min(2, s + 0.05))}
            className="px-2.5 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-colors border-l border-navy/10 font-medium">+</button>
        </div>
      </div>

      {/* Clarity Statement — dynamic restatement of the Nested Egg in plain language */}
      {bigIdea && scaffold1 && scaffold2 && (
        <ClarityStatement
          measurement={measurement}
          bigIdea={bigIdea.text}
          scaffold1={scaffold1.text}
          scaffold2={scaffold2.text}
          proof1={tiles.filter((t) => t.zone === 'PROOF_1').map((t) => t.text)}
          proof2={tiles.filter((t) => t.zone === 'PROOF_2').map((t) => t.text)}
        />
      )}
    </div>
  );
}

// Lowercase the first character so user input reads naturally mid-sentence.
// Preserves the rest (proper nouns, acronyms stay as the user typed them).
function lowerFirst(str) {
  if (!str) return '';
  // Preserve acronyms: if first 2 chars are both uppercase letters (AI, ROI, KPI)
  if (/^[A-Z]{2}/.test(str)) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}

// Detect if a string starts with a verb (common action words)
const VERB_START = /^(increase|improve|reduce|create|build|achieve|ensure|drive|get|make|deliver|grow|launch|secure|establish|develop|implement|generate|expand|maximize|minimize|optimize|strengthen|accelerate|transform|enable|empower|earn|enter|save|cut|boost|raise|lower|maintain|sustain|reach|hit|exceed|double|triple|prove|show|demonstrate|win|close|convert|retain|acquire|upsell|automate|streamline|simplify|eliminate|prevent|protect|scale|align|integrate|unify|consolidate|engage|activate|inspire|motivate|educate|train|teach|convince|persuade)\b/i;


function deliverPhrase(metric) {
  if (!metric) return null;
  const m = lowerFirst(metric);
  if (VERB_START.test(m)) return <>This is designed to <strong>{m}</strong>. </>;
  return <>This delivers <strong>{m}</strong>. </>;
}

function scaffoldPhrase(scaffold) {
  return <><strong>{lowerFirst(scaffold)}</strong></>;
}

function formatConceptList(concepts) {
  if (concepts.length === 0) return null;
  return concepts.map((c, i, arr) => (
    <span key={i}>
      {i > 0 && i < arr.length - 1 && ', '}
      {i > 0 && i === arr.length - 1 && ', and '}
      {lowerFirst(c)}
    </span>
  ));
}

function ClarityStatement({ measurement, bigIdea, scaffold1, scaffold2, proof1, proof2 }) {
  const allConcepts = [...proof1, ...proof2];
  return (
    <div className="flex-shrink-0 bg-white border-t-2 border-gold px-6 py-4">
      <p className="text-[10px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-2">
        Your Clarity Statement
      </p>
      <p className="text-sm text-txt-primary leading-relaxed font-body">
        {deliverPhrase(measurement)}
        The Big Idea: <strong>{lowerFirst(bigIdea)}</strong>.
        {' '}It's built on two scaffolds: {scaffoldPhrase(scaffold1)}, and {scaffoldPhrase(scaffold2)}.
        {allConcepts.length > 0 && <> Supporting concepts: {formatConceptList(allConcepts)}.</>}
      </p>
    </div>
  );
}
